<template>
  <div class="blueprint">
    <!-- 지금 상태로는 발동 불가한 이유. 시나리오는 draft 로 생성되고 스스로 돌지 않는다. -->
    <a-alert
      v-if="blueprint.blockers.length"
      type="error"
      show-icon
      class="blueprint-alert"
      message="이 시나리오는 draft 로 생성되며 발동하지 않습니다"
    >
      <template #description>
        <ul class="reason-list">
          <li v-for="(blocker, index) in blueprint.blockers" :key="index">{{ blocker }}</li>
        </ul>
      </template>
    </a-alert>

    <!-- 발동은 하되 조용히 틀어질 수 있는 위험 -->
    <a-alert
      v-if="blueprint.risks.length"
      type="warning"
      show-icon
      class="blueprint-alert"
      message="확인이 필요한 위험"
    >
      <template #description>
        <ul class="reason-list">
          <li v-for="(risk, index) in blueprint.risks" :key="index">{{ risk }}</li>
        </ul>
      </template>
    </a-alert>

    <div class="blueprint-head">
      <h5 class="blueprint-name">{{ blueprint.name }}</h5>
      <a-tag color="default">draft</a-tag>
      <a-tag :color="blueprint.sourceIsActive ? 'green' : 'default'">
        AfterDoc {{ blueprint.sourceIsActive ? "활성" : "비활성" }}
      </a-tag>
      <span class="muted">{{ blueprint.steps.length }}단계</span>
    </div>

    <!-- ===== 트리거 ===== -->
    <div class="node trigger-node">
      <div class="node-badge">
        <ThunderboltOutlined />
        <span>트리거</span>
      </div>
      <div class="node-body">
        <div class="node-title">{{ triggerHeadline }}</div>
        <div class="tag-chips">
          <a-tag v-for="tagName in blueprint.trigger.tagNames" :key="tagName" color="blue">{{ tagName }}</a-tag>
          <span v-if="!blueprint.trigger.tagNames.length" class="muted">태그 없음</span>
        </div>
        <div class="node-meta">
          <code>{{ blueprint.trigger.field }}</code> {{ blueprint.trigger.op }}
        </div>
      </div>
    </div>

    <!-- ===== 발송 체인 ===== -->
    <div v-for="step in blueprint.steps" :key="step.index" class="node step-node">
      <div class="node-badge timing-badge">
        <span class="timing-offset">{{ formatOffset(step.timing) }}</span>
        <span class="timing-time">{{ step.timing.timeOfDay || "--:--" }}</span>
      </div>
      <div class="node-body">
        <div class="node-title">{{ step.name }}</div>

        <!-- D+N 은 기준일이 있어야 계산된다. sourceDatePath 가 없으면 대기 자체가 불가능하다. -->
        <div class="node-row">
          <span class="row-label">대기</span>
          <span v-if="step.timing.sourceDatePath" class="row-value">
            기준일 <code>{{ step.timing.sourceDatePath }}</code> 로부터
            {{ step.timing.offsetDays }}일 {{ step.timing.offsetDirection === "before" ? "전" : "후" }}
            {{ step.timing.timeOfDay }}
          </span>
          <span v-else class="row-value warn-text">
            <WarningOutlined />
            기준일(시술일)이 해석되지 않아 D+{{ step.timing.offsetDays }} 대기 시점을 계산할 수 없습니다
          </span>
        </div>

        <div class="node-row">
          <span class="row-label">템플릿</span>
          <span class="row-value">
            {{ step.templateName }}
            <a-tag v-if="step.adopted" color="cyan">기존 채택 {{ step.templateCode }}</a-tag>
            <a-tag v-else-if="step.templateCode" color="blue">{{ step.templateCode }}</a-tag>
            <a-tag v-else color="orange">신규 등록 예정</a-tag>
          </span>
        </div>

        <div class="node-row">
          <span class="row-label">변수</span>
          <span class="row-value">
            <span v-if="!step.variables.length" class="muted">없음</span>
            <span v-else class="var-chips">
              <a-tooltip v-for="variable in step.variables" :key="variable.name" :title="variableBinding(variable)">
                <a-tag :color="variable.resolvable ? 'green' : 'orange'">
                  {{ variable.name }}
                  <!-- 무엇이 박히는지 칩에 그대로 노출한다. 툴팁에 숨기면 검증을 못 한다. -->
                  <span v-if="variable.source === 'static'" class="var-bind">= {{ variable.value }}</span>
                  <span v-else-if="variable.source === 'match'" class="var-bind">
                    = 편입시각<template v-if="variable.offsetDays">+{{ variable.offsetDays }}일</template>
                  </span>
                  <span v-else-if="variable.source === 'matched_tag'" class="var-bind">
                    <template v-if="variable.candidates && variable.candidates.length === 1">= {{ variable.candidates[0] }}</template>
                    <template v-else>= 매칭 태그</template>
                  </span>
                  <WarningOutlined v-if="!variable.resolvable" />
                </a-tag>
              </a-tooltip>
            </span>
          </span>
        </div>

        <!-- 툴팁만으로는 못 찾는 사람이 있어 미해석 사유는 화면에 그대로 남긴다 -->
        <ul v-if="unresolvableVariables(step).length" class="var-notes">
          <li v-for="variable in unresolvableVariables(step)" :key="variable.name">
            <strong>{{ variable.name }}</strong>: {{ variable.note || "발송 시 값이 채워지지 않습니다" }}
          </li>
        </ul>
      </div>
    </div>

    <!-- 개발자 확인용. 기본은 접어둔다. -->
    <a-collapse class="raw-collapse">
      <a-collapse-panel key="blocks" header="원본 blocks (개발자 확인용)">
        <pre class="raw-json">{{ rawBlocks }}</pre>
      </a-collapse-panel>
    </a-collapse>
  </div>
</template>

<script>
import { computed } from "vue";
import { ThunderboltOutlined, WarningOutlined } from "@ant-design/icons-vue";

// 변수 바인딩 출처 표기
const SOURCE_TEXT_MAP = {
  customer_snapshot: "고객",
  hospital: "병원",
  source_snapshot: "원본",
  // 편입(매칭) 시각 = 태그가 CRM 에 동기화된 순간. AfterDoc 의 "시술일" 앵커.
  match: "편입시각",
  // 시나리오 태그가 1개뿐이라 상수로 박은 값
  static: "고정값",
  // 다중 태그 시나리오에서 실제로 매칭된 태그명 (발송 시점 결정)
  matched_tag: "매칭 태그",
};

export default {
  name: "ScenarioBlueprint",
  components: { ThunderboltOutlined, WarningOutlined },
  props: {
    blueprint: { type: Object, required: true },
  },
  setup(props) {
    const triggerHeadline = computed(() => {
      const { matchMode, tagNames } = props.blueprint.trigger;
      if (matchMode === "or" && tagNames.length > 1) {
        return "고객 태그에 다음 중 하나라도 포함 (OR)";
      }
      const first = tagNames[0];
      return first ? `고객 태그에 "${first}" 포함` : "고객 태그 조건 없음";
    });

    // D+N / D-N. 발송 대기가 한눈에 읽혀야 한다.
    const formatOffset = (timing) => {
      const sign = timing.offsetDirection === "before" ? "-" : "+";
      return `D${sign}${timing.offsetDays}`;
    };

    const variableBinding = (variable) => {
      if (!variable.resolvable) return variable.note || "발송 시 값이 채워지지 않습니다";
      const source = SOURCE_TEXT_MAP[variable.source] || variable.source;
      // 상수로 박힌 값은 무엇이 박혔는지 그대로 보여줘야 검증이 된다 ("고정값." 만 보면 알 수 없다)
      if (variable.source === "static") {
        return `${variable.name} → ${source} "${variable.value}"`;
      }
      if (variable.source === "matched_tag") {
        const cands = (variable.candidates || []).join(", ");
        return `${variable.name} → ${source} (${cands} 중 편입 시 매칭된 것)`;
      }
      const offset = variable.offsetDays ? ` +${variable.offsetDays}일` : "";
      return `${variable.name} → ${source}.${variable.field}${offset}`;
    };

    const unresolvableVariables = (step) => step.variables.filter((variable) => !variable.resolvable);

    const rawBlocks = computed(() => JSON.stringify(props.blueprint.blocks, null, 2));

    return { triggerHeadline, formatOffset, variableBinding, unresolvableVariables, rawBlocks };
  },
};
</script>

<style scoped>
.blueprint-alert {
  margin-bottom: 12px;
}

.blueprint-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.blueprint-name {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

/* 트리거 -> 메시지 체인을 세로 흐름으로 읽히게 한다 */
.node {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 12px 0 12px 0;
}

.node::before {
  content: "";
  position: absolute;
  left: 34px;
  top: 40px;
  bottom: -12px;
  width: 2px;
  background: #e8e8e8;
}

.node:last-of-type::before {
  display: none;
}

.node-badge {
  flex: 0 0 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 52px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  color: #555;
  z-index: 1;
}

.trigger-node .node-badge {
  background: #fffbe6;
  border-color: #ffe58f;
  color: #ad6800;
}

.timing-badge .timing-offset {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.timing-badge .timing-time {
  font-size: 11px;
  color: #888;
}

.node-body {
  flex: 1;
  min-width: 0;
}

.node-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.node-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #999;
}

.node-row {
  display: flex;
  gap: 8px;
  line-height: 1.9;
  font-size: 13px;
}

.row-label {
  flex: 0 0 48px;
  color: #999;
}

.row-value {
  flex: 1;
  min-width: 0;
  color: #555;
}

.warn-text {
  color: #d46b08;
}

/* 태그/변수 칩이 길어져도 페이지가 가로로 밀리면 안 된다 */
.tag-chips,
.var-bind {
  margin-left: 4px;
  opacity: 0.75;
  font-weight: 400;
}

.var-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
}

.var-notes {
  margin: 6px 0 0;
  padding: 8px 12px 8px 28px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  font-size: 12px;
  color: #874d00;
}

.raw-collapse {
  margin-top: 16px;
}

.raw-json {
  margin: 0;
  max-height: 360px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.6;
  color: #333;
}

.muted {
  color: #999;
}

code {
  font-family: monospace;
  font-size: 12px;
  color: #555;
  background: #f5f5f5;
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
